<template>
    <div class="main-layout__container">
        <div class="content__header">
            <div class="content-header__title">Отчет о размещении и продаже</div>

            <div class="content-header__background content-header__background--1-line-filters"></div>
        </div>

        <FilterPanel
            @search="onSearch"
            @reset="onReset"
        >
            <UISelect
                id="year"
                v-model="reportsFilter.year"
                label="Год"
                :options="yearOptions"
            />

            <UISelect
                id="month"
                v-model="reportsFilter.month"
                label="Месяц"
                :options="monthOptions"
            />

            <UISelect
                id="dealer_id"
                v-model="reportsFilter.dealer_id"
                :options="dealerOptions"
                label="Дилер"
                searchable
            />
        </FilterPanel>

        <UILoader v-if="isCarPlacementSaleReportsLoading" class="tableable" />

        <div v-else class="content__body">
            <DataTable
                :headers="headers"
                :data="arCarPlacementSaleReports"
                :sort-key="sortKey"
                :sort-order="sortOrder"
                has-actions
                @sortable="changeSort"
            >
                <template #dealer_name="{ row }">
                    {{ row.dealer.name }} ({{ row.dealer.sap_code }})
                </template>

                <template #report_period="{ row }">
                    {{ getReportPeriodColumn(row as CarPlacementSaleReport) }}
                </template>

                <template #created_at="{ row }">
                    {{ formatFromUTC(row.created_at) }}
                </template>

                <template #updated_at="{ row }">
                    {{ formatFromUTC(row.updated_at) }}
                </template>

                <template #dealer_email="{ row }">
                    {{ row.dealer.email }}
                </template>

                <template #actions="{ row }">
                    <div class="report-placement-sale__table-actions">
                        <div
                            class="report-placement-sale__table-actions-item"
                            @click.stop="onDownload(row as CarPlacementSaleReport)"
                        >
                            <img v-svg-inline src="@/assets/icons/ui/download.svg" />
                        </div>

                        <UIDropDown as-modal-on-mobile>
                            <template #elements>
                                <li
                                    class="ui-dropdown-menu__element"
                                    @click.stop="onRegenerateReport(row as CarPlacementSaleReport)"
                                >
                                    <img v-svg-inline src="@/assets/icons/ui/update.svg" />
                                    <span>Сформировать повторно</span>
                                </li>
                            </template>
                        </UIDropDown>
                    </div>
                </template>
            </DataTable>
        </div>

        <div class="content__footer">
            <TablePagination
                :total-pages="carPlacementSaleReportsPaginationMeta?.pagination.last_page ?? 1"
                :current-page="carPlacementSaleReportsPaginationMeta?.pagination.current_page ?? 1"
                :items-per-page="carPlacementSaleReportsPaginationMeta?.pagination.per_page ?? 1"
                @page-change="changePage"
            />
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { computed, h, inject, onMounted, ref } from 'vue';

    import { ModalPlugin } from '@/plugins/modalPlugin.ts';

    import { SelectOption } from '@/types/form.type.ts';
    import { DealerListItem } from '@/types/dealer.type.ts';
    import { CarPlacementSaleReport, CarPlacementSaleReportsRequestFilter } from '@/types/report.type.ts';

    import useDealerRepository from '@/repositories/dealer/useDealerRepository.ts';
    import useDocumentDownloader from '@/utils/useDocumentDownloader.ts';
    import useNotifications from '@/utils/useNotifications.ts';
    import useReportRepository from '@/repositories/report/useReportRepository.ts';
    import { useCarPlacementSaleReportsFilterStore } from '@/stores/reports/car-placement-sale-reports-filter.ts';

    import { formatFromUTC, getMonthNameByNumber } from '@/utils/DateTimeFormatter.ts';
    import { getReportMonthOptions, getReportYearOptions } from '@/utils/SelectOptionFillers.ts';

    import DataTable from '@/components/shared/DataTable/DataTable.vue';
    import FilterPanel from '@/components/shared/FilterPanel/FilterPanel.vue';
    import UILoader from '@/components/ui/UILoader/UILoader.vue';
    import TablePagination from '@/components/shared/TablePagination/TablePagination.vue';

    import UIDropDown from '@/components/ui/UIDropDown/UIDropDown.vue';
    import UISelect from '@/components/ui/UISelect/UISelect.vue';

    import ConfirmActionModal from '@/components/modals/ConfirmActionModal/ConfirmActionModal.vue';

    import ReportPlacementSaleConfirmRegenerate from '@/components/report/ReportPlacementSaleConfirmRegenerate/ReportPlacementSaleConfirmRegenerate.vue';

    const $modal = inject('$modal') as typeof ModalPlugin;

    const {
        arCarPlacementSaleReports,
        carPlacementSaleReportsPaginationMeta,
        isCarPlacementSaleReportsLoading,
        getCarsPostingAndSellingReports,
        regenerateCarsPostingAndSellingReport,
    } = useReportRepository();
    const { getAllActiveDealers } = useDealerRepository();
    const { downloadCarPlacementSaleReport } = useDocumentDownloader();
    const { showSuccess } = useNotifications();
    const reportsFilterStore = useCarPlacementSaleReportsFilterStore();

    const reportsFilter = ref<CarPlacementSaleReportsRequestFilter>({ ...reportsFilterStore.$state });
    const yearOptions = ref<SelectOption[]>(getReportYearOptions());
    const dealerOptions = ref<SelectOption[]>([]);
    const headers = ref([
        { label: 'Дилер', key: 'dealer_name', sortable: true },
        { label: 'Период', key: 'report_period', sortable: true },
        { label: 'Добавлен', key: 'created_at', sortable: true },
        { label: 'Обновлен', key: 'updated_at', sortable: true },
        { label: 'Email сотрудника', key: 'dealer_email' },
        { label: '', key: '' },
    ]);

    const monthOptions = computed<SelectOption[]>(() => {
        if (reportsFilter.value.year === undefined) {
            return [];
        }

        return getReportMonthOptions(reportsFilter.value.year);
    });

    const sortKey = computed<string | undefined>(() => {
        if (reportsFilter.value.sort) {
            return Object.keys(reportsFilter.value.sort[0] ?? {})[0];
        }

        return undefined;
    });

    const sortOrder = computed<string | undefined>(() => {
        if (reportsFilter.value.sort) {
            return Object.values(reportsFilter.value.sort[0] ?? {})[0];
        }

        return undefined;
    });

    function onSearch(): void {
        reportsFilter.value.page = 1;
        fetchReports();
    }

    function onReset(): void {
        reportsFilter.value = {
            year: undefined,
            month: undefined,
            dealer_id: undefined,
            page: 1,
            sort: reportsFilter.value.sort,
        };
    }

    function changePage(page: number): void {
        reportsFilter.value.page = page;
        fetchReports();
    }

    function changeSort(key: string, dir: 'asc' | 'desc' | undefined): void {
        reportsFilter.value.sort = dir === undefined ? [{ dealer_name: 'asc' }] : [{ [key]: dir }];
        fetchReports();
    }

    function onDownload(report: CarPlacementSaleReport): void {
        downloadCarPlacementSaleReport(report.id, report.file_name);
    }

    function onRegenerateReport(report: CarPlacementSaleReport): void {
        $modal.open({
            component: ConfirmActionModal,
            modalType: 'narrow',
            slots: {
                default: () =>
                    h(ReportPlacementSaleConfirmRegenerate, { report }),
            },
            props: {
                onConfirm: async () => {
                    await regenerateCarsPostingAndSellingReport(report.id);

                    showSuccess({
                        title: 'Отчет сформирован повторно',
                    });

                    fetchReports();
                },
            },
        });
    }

    function getReportPeriodColumn(report: CarPlacementSaleReport): string {
        return `${getMonthNameByNumber(report.month)} ${report.year}`;
    }

    function mapReportsFilterToRequestFilter(): CarPlacementSaleReportsRequestFilter {
        const requestFilter: CarPlacementSaleReportsRequestFilter = {
            page: reportsFilter.value.page,
            sort: reportsFilter.value.sort,
        };

        if (reportsFilter.value.dealer_id !== undefined) {
            requestFilter.dealer_id = reportsFilter.value.dealer_id;
        }

        if (reportsFilter.value.year !== undefined) {
            requestFilter.year = reportsFilter.value.year;
        }

        if (reportsFilter.value.month !== undefined) {
            requestFilter.month = reportsFilter.value.month;
        }

        return requestFilter;
    }

    function fetchReports(): void {
        reportsFilterStore.$patch({ ...reportsFilter.value });
        getCarsPostingAndSellingReports(mapReportsFilterToRequestFilter());
    }

    function fetchFilterOptions(): void {
        getAllActiveDealers().then((dealers: DealerListItem[]) => {
            dealerOptions.value = dealers.map((dealer: DealerListItem) => ({
                label: `${dealer.name} (${dealer.sap_code})`,
                value: dealer.id,
            }));
        });

        for (let year = (new Date()).getFullYear(); year >= 2020; year--) {
            yearOptions.value.push({
                label: year,
                value: year,
            });
        }
    }

    onMounted(() => {
        fetchReports();
        fetchFilterOptions();
    });
</script>

<style scoped lang="scss">
    @use './ReportPlacementSale.scss' as *;
</style>
